import React, { useState, useCallback } from "react"
import { ChevronDown, ChevronRight, Pencil, Trash2, Check, X, Power } from "lucide-react"
import { Button } from "src/components/ui/button"
import { Input } from "src/components/ui/input"
import { Badge } from "src/components/ui/badge"
import { Switch } from "src/components/ui/switch"
import { useToast } from "src/components/ui/toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "src/components/ui/alert-dialog"
import { StageRow } from "./StageRow"
import { AddStageForm } from "./AddStageForm"
import {
  useUpdatePipeline,
  useDeletePipeline,
  useActivatePipeline,
  useCreateStage,
  useUpdateStage,
  useDeleteStage,
} from "../hooks/usePipelines"

const ENTITY_LABELS = { deal: "Deal", lead: "Lead" }

export function PipelineCard({ pipeline, onRefresh }) {
  const { toast } = useToast()
  const [expanded, setExpanded] = useState(pipeline.active)
  const [editingName, setEditingName] = useState(false)
  const [name, setName] = useState(pipeline.name)

  const { mutate: doUpdate, isPending: isUpdating } = useUpdatePipeline()
  const { mutate: doDelete, isPending: isDeleting } = useDeletePipeline()
  const { mutate: doActivate, isPending: isActivating } = useActivatePipeline()
  const { mutate: doCreateStage, isPending: isCreating } = useCreateStage()
  const { mutate: doUpdateStage, isPending: stageUpdating } = useUpdateStage()
  const { mutate: doDeleteStage, isPending: stageDeleting } = useDeleteStage()

  const handleRename = async () => {
    if (!name.trim() || name === pipeline.name) { setEditingName(false); return }
    try {
      await doUpdate(pipeline.id, { name: name.trim() })
      toast({ title: "Pipeline renamed" })
      onRefresh()
    } catch (errs) {
      toast({ title: "Error", description: String(errs), variant: "destructive" })
    }
    setEditingName(false)
  }

  const handleActivate = async () => {
    try {
      await doActivate(pipeline.id)
      toast({ title: `${pipeline.name} is now active` })
      onRefresh()
    } catch (errs) {
      toast({ title: "Error", description: String(errs), variant: "destructive" })
    }
  }

  // const handleToggleActive = async (checked) => {
  //   try {
  //     if (checked) {
  //       await doActivate(pipeline.id)
  //       toast({ title: `${pipeline.name} is now active` })
  //     } else {
  //       await doUpdate(pipeline.id, { active: false, is_default: false })
  //       toast({ title: `${pipeline.name} is now inactive` })
  //     }
  //     onRefresh()
  //   } catch (errs) {
  //     toast({ title: "Error", description: String(errs), variant: "destructive" })
  //   }
  // }

  const handleDelete = async () => {
    try {
      await doDelete(pipeline.id)
      toast({ title: "Pipeline deleted" })
      onRefresh()
    } catch (errs) {
      toast({ title: "Error", description: String(errs), variant: "destructive" })
    }
  }

  const handleAddStage = useCallback(async (payload) => {
    try {
      await doCreateStage(pipeline.id, payload)
      toast({ title: "Stage added" })
      onRefresh()
    } catch (errs) {
      toast({ title: "Error", description: String(errs), variant: "destructive" })
      throw errs
    }
  }, [pipeline.id, doCreateStage, toast, onRefresh])

  const handleUpdateStage = useCallback(async (stageId, payload) => {
    try {
      await doUpdateStage(pipeline.id, stageId, payload)
      toast({ title: "Stage updated" })
      onRefresh()
    } catch (errs) {
      toast({ title: "Error", description: String(errs), variant: "destructive" })
      throw errs
    }
  }, [pipeline.id, doUpdateStage, toast, onRefresh])

  const handleDeleteStage = useCallback(async (stageId) => {
    try {
      await doDeleteStage(pipeline.id, stageId)
      toast({ title: "Stage removed" })
      onRefresh()
    } catch (errs) {
      toast({ title: "Error", description: String(errs), variant: "destructive" })
    }
  }, [pipeline.id, doDeleteStage, toast, onRefresh])

  return (
    <div className={`rounded-xl border ${pipeline.active ? "border-indigo-200 bg-white shadow-sm" : "border-slate-100 bg-slate-50/50"} transition-all`}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => setExpanded(!expanded)} className="shrink-0 text-slate-400 hover:text-slate-600">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>

        {editingName ? (
          <div className="flex items-center gap-1.5">
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-7 w-48 text-sm" autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") handleRename(); if (e.key === "Escape") { setName(pipeline.name); setEditingName(false) } }}
            />
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={handleRename} disabled={isUpdating}>
              <Check className="h-3.5 w-3.5 text-emerald-600" />
            </Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setName(pipeline.name); setEditingName(false) }}>
              <X className="h-3.5 w-3.5 text-slate-400" />
            </Button>
          </div>
        ) : (
          <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800 cursor-pointer" onDoubleClick={() => setEditingName(true)}>
            {pipeline.name}
          </span>
        )}

        <Badge variant="outline" className="text-[11px]">{ENTITY_LABELS[pipeline.entity_type] || pipeline.entity_type}</Badge>
        <span className="text-xs text-slate-400">{pipeline.stages?.length || 0} stages</span>

        {pipeline.active ? (
          <Badge className="bg-emerald-100 text-emerald-700 text-[11px]">Active</Badge>
        ) : (
          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={handleActivate} disabled={isActivating}>
            <Power className="h-3 w-3" /> Activate
          </Button>
        )}

        {/* <div className="flex items-center gap-1.5 bg-slate-100/60 rounded-lg px-2 py-0.5 border border-slate-200/50">
          <span className="text-[11px] font-medium text-slate-500">Active</span>
          <Switch
            checked={pipeline.active}
            onCheckedChange={handleToggleActive}
            disabled={isActivating || isUpdating}
            className="scale-75 origin-right"
          />
        </div> */}

        <div className="flex gap-0.5">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setEditingName(true)}>
            <Pencil className="h-3.5 w-3.5 text-slate-400" />
          </Button>
          {!pipeline.is_default && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={isDeleting}>
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete pipeline?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently remove "{pipeline.name}" and all its stages.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {/* <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={isDeleting}>
                <Trash2 className="h-3.5 w-3.5 text-red-400" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              {pipeline.is_default ? (
                <>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cannot delete default pipeline</AlertDialogTitle>
                    <AlertDialogDescription>
                      "{pipeline.name}" is the default pipeline for {ENTITY_LABELS[pipeline.entity_type] || pipeline.entity_type}s and cannot be deleted. You must make another pipeline the default first.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogAction>OK</AlertDialogAction>
                  </AlertDialogFooter>
                </>
              ) : (
                <>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete pipeline?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently remove "{pipeline.name}" and all its stages.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </>
              )}
            </AlertDialogContent>
          </AlertDialog> */}
        </div>
      </div>

      {/* Stages list */}
      {expanded && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-1.5">
          {pipeline.stages?.length === 0 && (
            <p className="py-4 text-center text-sm text-slate-400">No stages yet</p>
          )}
          {pipeline.stages?.map((stage) => (
            <StageRow
              key={stage.id}
              stage={stage}
              onUpdate={handleUpdateStage}
              onDelete={handleDeleteStage}
              isUpdating={stageUpdating}
              isDeleting={stageDeleting}
              dragHandleProps={{}}
            />
          ))}
          <AddStageForm onAdd={handleAddStage} isPending={isCreating} />
        </div>
      )}
    </div>
  )
}
